<!--
  StorePolicyEditor.vue -- one store policy (Privacy or Refund) in the store
  editor's Tax & Legal section.

  Jake, 2026-10-19 (policy updates): per policy the store has

    * a LINK LABEL -- the footer link text AND the title of the policy page;
      blank means the default ("Privacy Policy" / "Refund Policy");
    * a MODE toggle -- "Link to your own URL" or "Show a policy page on this
      store" (a hosted rich-text page at /shop/<slug>/<kind>-policy);
    * the URL (shown in URL mode) and the rich-text body (shown in page
      mode). Switching modes keeps the other value, nothing is erased.

  The distributor-level default URLs are gone: this is the only place a
  policy is set.

  Edits mutate the `campaign` prop in place and emit `campaign-changed`, the
  section pattern the editor's autosave listens for (same as StoreBranding's
  welcome message). The loader (getCampaignById) returns every key written
  here; a missing key reads as the defaults (hosted-page mode, no label).

  COLLAPSE (Bug Intake #458): policies are set once and rarely edited, so a
  hosted page that already has saved text starts COLLAPSED -- a short plain-text
  preview and an Edit button -- instead of a full-height editor that pushes the
  other Tax & Legal settings screens down. A policy with no text starts open
  (nothing to preview) and STAYS open while the first words are typed. Choosing
  "Show a policy page" always opens the editor: the author just said they are
  about to write it. Collapsing never touches the text or the autosave.

  The rich-text editor is the shared RichTextEditor with inline images FORCED
  OFF: a policy page holds no images, and both sanitizers strip <img>, so
  offering the button would let an author insert something that vanishes on
  save.
-->
<template>
  <div class="spe" :data-testid="`ctl-legal-${kind}-root`">
    <h4 class="spe__title">{{ defaultLabel }}</h4>

    <div class="spe__field">
      <label class="spe__label" :for="`ctl-legal-${kind}-label-input`">
        Link label
      </label>
      <v-text-field
        :id="`ctl-legal-${kind}-label-input`"
        :model-value="label"
        :placeholder="defaultLabel"
        :disabled="!editMode"
        density="compact"
        variant="outlined"
        hide-details
        maxlength="120"
        :data-testid="`ctl-legal-${kind}-label`"
        @update:modelValue="onLabelInput"
      />
      <p class="spe__help" :data-testid="`ctl-legal-${kind}-label-help`">
        The text shoppers see in the store footer, and the title of the page
        if you host it here. Leave it blank to use "{{ defaultLabel }}".
      </p>
    </div>

    <div class="spe__field">
      <div class="spe__label" :id="`ctl-legal-${kind}-mode-label`">
        What the footer link opens
      </div>
      <div
        class="spe__modes"
        role="radiogroup"
        :aria-labelledby="`ctl-legal-${kind}-mode-label`"
      >
        <label class="spe__mode">
          <input
            type="radio"
            :name="`ctl-legal-${kind}-mode`"
            value="url"
            :checked="mode === 'url'"
            :disabled="!editMode"
            :data-testid="`ctl-legal-${kind}-mode-url`"
            @change="setMode('url')"
          />
          <span>Link to your own URL</span>
        </label>
        <label class="spe__mode">
          <input
            type="radio"
            :name="`ctl-legal-${kind}-mode`"
            value="page"
            :checked="mode === 'page'"
            :disabled="!editMode"
            :data-testid="`ctl-legal-${kind}-mode-page`"
            @change="setMode('page')"
          />
          <span>Show a policy page on this store</span>
        </label>
      </div>
      <p class="spe__help" :data-testid="`ctl-legal-${kind}-mode-help`">
        Choose whether the link goes to a page you host elsewhere, or to a
        page ThreadFlow shows on your store at
        <code>/shop/&lt;your-store&gt;/{{ kind }}-policy</code>.
      </p>
    </div>

    <div v-if="mode === 'url'" class="spe__field">
      <label class="spe__label" :for="`ctl-legal-${kind}-url-input`">
        {{ defaultLabel }} URL
      </label>
      <v-text-field
        :id="`ctl-legal-${kind}-url-input`"
        :model-value="url"
        :disabled="!editMode"
        :error-messages="urlError ? [urlError] : []"
        density="compact"
        variant="outlined"
        hide-details="auto"
        :placeholder="kind === 'privacy' ? 'https://example.com/privacy' : 'https://example.com/refunds'"
        :data-testid="`ctl-legal-${kind}-url`"
        @update:modelValue="onUrlInput"
      />
      <p class="spe__help" :data-testid="`ctl-legal-${kind}-url-help`">
        The full web address of your policy. A blank field hides the link.
      </p>
    </div>

    <div v-else class="spe__field">
      <div class="spe__page-head">
        <div class="spe__label">{{ defaultLabel }} page</div>
        <button
          v-if="hasText && expanded"
          type="button"
          class="spe__toggle"
          :data-testid="`ctl-legal-${kind}-collapse`"
          @click="expanded = false"
        >
          Collapse
        </button>
      </div>
      <div
        v-if="!expanded"
        class="spe__collapsed"
        :data-testid="`ctl-legal-${kind}-collapsed`"
      >
        <p class="spe__preview" :data-testid="`ctl-legal-${kind}-preview`">{{ preview }}</p>
        <button
          type="button"
          class="spe__toggle"
          :data-testid="`ctl-legal-${kind}-expand`"
          @click="expanded = true"
        >
          {{ editMode ? "Edit policy" : "Show policy" }}
        </button>
      </div>
      <rich-text-editor
        v-else
        :model-value="html"
        :read-only="!editMode"
        :images-enabled-override="false"
        :data-testid="`ctl-legal-${kind}-editor`"
        @update:modelValue="onHtmlInput"
      />
      <p class="spe__help" :data-testid="`ctl-legal-${kind}-editor-help`">
        Write the policy here. It is shown to shoppers on its own page, with
        your store's header and footer. An empty page hides the footer link.
      </p>
    </div>
  </div>
</template>

<script>
import RichTextEditor from "@/components/RichTextEditor.vue";
import { sanitizeExternalUrl } from "@/utils/sanitizeExternalUrl";
import { DEFAULT_POLICY_LABELS } from "@/utils/storefrontPolicyLinks";

const PREVIEW_CHARS = 160;

// The visible text of a stored policy page, as a plain string. DOMParser builds
// an inert document (no script runs, no image loads), and textContent drops
// every tag; the result is only ever bound with {{ }}, never v-html.
function plainText(html) {
  if (typeof html !== "string" || !html) return "";
  // Block boundaries become spaces so "<h2>Title</h2><p>Body" reads "Title Body".
  const spaced = html.replace(/<\/?(p|h[1-6]|li|ul|ol|div|blockquote|br|hr)\b[^>]*>/gi, " ");
  const doc = new DOMParser().parseFromString(spaced, "text/html");
  doc.querySelectorAll("script, style").forEach((n) => n.remove());
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

function hasPageText(html) {
  return plainText(html).length > 0;
}

function previewText(html) {
  const text = plainText(html);
  return text.length > PREVIEW_CHARS
    ? `${text.slice(0, PREVIEW_CHARS).trimEnd()}...`
    : text;
}

export default {
  name: "StorePolicyEditor",
  components: { "rich-text-editor": RichTextEditor },
  props: {
    // "privacy" | "refund"
    kind: { type: String, required: true },
    campaign: { type: Object, required: true },
    editMode: { type: Boolean, default: false },
  },
  emits: ["campaign-changed"],
  data() {
    return {
      // Open when there is nothing to preview; collapsed once text is saved.
      // Read once at mount (the editor mounts after the campaign loads), then
      // owned by the author's clicks -- typing never collapses it.
      expanded: !hasPageText(this.campaign?.[`${this.kind}_policy_html`]),
    };
  },
  computed: {
    defaultLabel() {
      return DEFAULT_POLICY_LABELS[this.kind];
    },
    label() {
      return this.campaign?.[`${this.kind}_policy_label`] || "";
    },
    // The hosted page is the default (Bug Intake #295): a missing key (a
    // payload that predates the column, or a brand-new store whose campaign
    // object has not been saved yet) reads as page mode, matching the model
    // default for new stores. Only an explicit "url" is the external link.
    mode() {
      return this.campaign?.[`${this.kind}_policy_mode`] === "url"
        ? "url"
        : "page";
    },
    url() {
      return this.campaign?.[`${this.kind}_policy_url`] || "";
    },
    html() {
      return this.campaign?.[`${this.kind}_policy_html`] || "";
    },
    hasText() {
      return hasPageText(this.html);
    },
    preview() {
      return previewText(this.html);
    },
    // Inline only: the storefront hides a link it cannot trust, so tell the
    // author why theirs would not show.
    urlError() {
      const raw = this.url.trim();
      if (!raw) return "";
      return sanitizeExternalUrl(raw)
        ? ""
        : "Enter a full link that starts with http:// or https://";
    },
  },
  methods: {
    onLabelInput(val) {
      this.campaign[`${this.kind}_policy_label`] = val;
      this.$emit("campaign-changed");
    },
    setMode(mode) {
      this.campaign[`${this.kind}_policy_mode`] = mode;
      if (mode === "page") this.expanded = true;
      this.$emit("campaign-changed");
    },
    onUrlInput(val) {
      this.campaign[`${this.kind}_policy_url`] = val;
      this.$emit("campaign-changed");
    },
    onHtmlInput(val) {
      this.campaign[`${this.kind}_policy_html`] = val;
      this.$emit("campaign-changed");
    },
  },
};
</script>

<style scoped>
.spe + .spe {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid var(--tf-border, #e0e0e0);
}
.spe__title {
  margin: 0 0 12px;
  font: 600 15px var(--tf-font-sans, "Roboto", sans-serif);
  color: var(--tf-fg-1, #212121);
}
.spe__field {
  margin-bottom: 16px;
}
.spe__label {
  display: block;
  font: 500 13px var(--tf-font-sans, "Roboto", sans-serif);
  color: var(--tf-fg-1, #212121);
  margin-bottom: 6px;
}
.spe__help {
  font: 400 12px var(--tf-font-sans, "Roboto", sans-serif);
  color: var(--tf-fg-4, #757575);
  line-height: 1.5;
  margin: 6px 0 0;
}
.spe__page-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.spe__preview {
  margin: 0 0 8px;
  font: 400 13px/1.5 var(--tf-font-sans, "Roboto", sans-serif);
  color: var(--tf-fg-3, #616161);
  overflow-wrap: anywhere;
}
.spe__toggle {
  background: none;
  border: 0;
  padding: 0;
  font: 500 13px var(--tf-font-sans, "Roboto", sans-serif);
  color: var(--tf-primary, #1565c0);
  cursor: pointer;
}
.spe__toggle:hover {
  text-decoration: underline;
}
.spe__modes {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.spe__mode {
  display: flex;
  align-items: center;
  gap: 8px;
  font: 400 14px var(--tf-font-sans, "Roboto", sans-serif);
  cursor: pointer;
}
</style>
